/* eslint-disable import/no-extraneous-dependencies -- dayjs plugin subpaths */
import DateFormat from 'enum/dateFormat';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);

const LA_TZ = 'America/Los_Angeles';

export const MONTH_SHORT = 'MMM D, YYYY';
export const MONTH_LONG = 'MMMM D, YYYY';
export const TIME_SHORT = 'MMM Do YYYY hh:mm A';
export const TIME_SHORT_ALT = 'MM/DD/YY, h:mm a';
export const TIME_HOURS = 'HH:mm A';

/**
 * Pads number with leading zeros. The number of 0s is
 * set by numDigits which represents the number of digits a number
 * should have
 * @param num
 * @param numDigits
 * @returns
 */
export function padLeadingZeros(num: number, numDigits: number) {
  // convert number to string
  let s = `${num}`;

  /**
   * Keep adding leading zeros to the string as long as
   * the string length is less than the number of digits
   * we want
   */
  while (s.length < numDigits) s = `0${s}`;

  return s;
}

/**
 * Returns formatted date string. The date is convert to la timezone
 * @param date
 * @param format
 * @returns
 */
export const momentDateFormat = (
  date: Date,
  format: DateFormat,
): string => {
  const d = dayjs(date);
  if (!d.isValid()) {
    return 'Date not submitted';
  }
  return d.tz(LA_TZ).format(format);
};

/**
 * Table / list datetime in Pacific time, e.g. Jan 13th, 2026 7:00pm
 * (ordinal day + 12h clock, am/pm lowercase with no space before am/pm)
 */
export const formatTableDateTime = (
  date: string | number | Date | null | undefined,
): string => {
  const d = dayjs(date);
  if (!d.isValid()) {
    return 'Date not submitted';
  }
  const z = d.tz(LA_TZ);
  return `${z.format('MMM Do, YYYY h:mm')}${z.format('a')}`;
};

/**
 * Returns the number of years since date
 * @param date
 * @returns
 */
export const getAgeYears = (date: Date): number =>
  dayjs().diff(dayjs(date), 'year');

/**
 * Returns the time from elapsed from the provided date
 *
 * @param date The date object to calc elapsed time from
 * @returns A string with elapsed time from date
 */
export const momentFromNow = (date: Date) => dayjs(date).fromNow();

/**
 * Returns the provided date in detailed format
 * Sunday, Jan 1, 2021 at 7:30 PM
 * @param date
 * @returns
 */
export const momentDateDetailed = (date: Date) => {
  const part1 = dayjs(date).tz(LA_TZ).format('dddd, MMM D, YYYY');
  const part2 = dayjs(date).tz(LA_TZ).format('h:mm A');
  return `${part1} at ${part2}`;
};

/**
 * Returns a string with Hours which are calculated using
 * the provided minutes
 *
 * @param mins The minutes to convest to hours
 * @returns A string with the hours ex. '01:30'
 */
export const momentPaddedHrTime = (mins: number) => {
  // get the minutes that are left over
  const remainder = mins % 60;
  // get the hours
  const hrs = Math.floor(mins / 60);

  return `${padLeadingZeros(hrs, 2)}:${padLeadingZeros(remainder, 2)}`;
};

/**
 * Returns true if the current date is after the provided date
 *
 * @param date The date we want to compare to
 * @returns
 */
export const momentIsAfterDate = (date: Date, hours?: number) => {
  let compareDate = dayjs(date).tz(LA_TZ);

  if (hours) {
    compareDate = compareDate.add(5, 'hour');
  }

  return dayjs().tz(LA_TZ).isAfter(compareDate);
};

export const momentGetCurrentYear = (): string =>
  dayjs().tz(LA_TZ).format('YYYY');

/**
 * Parses a UTC instant (e.g. snapshot ISO string) to a JS Date for use with
 * momentDateFormat (same behavior as moment.utc(input).toDate()).
 */
export const utcTimestampToDate = (input: string | number | Date): Date =>
  dayjs.utc(input).toDate();
